import React from 'react';
import { TableRow, TableRowColumn, Checkbox, Avatar, FontIcon, MenuItem, IconButton } from 'material-ui';
import { SmallFlatButton, SmallRaisedButton } from '../../../tc-ui/small-buttons';
import ArrowDropDownIcon from 'material-ui/svg-icons/navigation/arrow-drop-down';
import FlatDropDownMenu from '../../../tc-ui/flatDropDownMenu';
import RedDot from '../../../tc-ui/redDot';
import { getShownState, getStatusColor, getIconEle, getSentState } from '../../contractingUtils';
import ActionButtons from './action-buttons';
import moment from 'moment';
import R from 'ramda';
import RatesReceivedIcon from 'material-ui/svg-icons/action/description';
import LockIcon from 'material-ui/svg-icons/action/lock-outline';
import SignedIcon from 'material-ui/svg-icons/editor/border-color';
import pluralize from 'pluralize';
import UpdateAssignee from '../../generic-components/update-assignee';

const ContractRowDumb =
  ({
     contract,
     selectedContracts,
     actions,
     showFolder,
     showContractYear,
     hideStatusIcon,
     forLandingPage,
     templates,
     billingPlan,
     openContacts,
     ...extraProps,
   }) => {
    const noReadAccess = !R.contains('read', contract.access || []);
    const {
      viewingBy,
      locationId,
      respondedContracts,
      emailStatus,
      viewerIsSender,
      partners,
      files,
      to = {},
      convertedFromExternal,
    } = contract;
    const tcContractsInRequest = Boolean(!locationId && respondedContracts && respondedContracts.length);
    const shownState = getShownState({ companyType: viewingBy, ...contract });
    const sentState = getSentState(contract.isViewed, contract.emailStatus);
    const hasVersion = !R.either(R.isNil, R.isEmpty)(contract.versions);
    const goIntoContractOrWizard = noReadAccess ? () => {} : () => {
      if (contract.viewingBy === 'Supplier' && !contract.locationId && !contract.buyerChecked) actions.goToSupplierWizard(contract._id);
      else actions.viewContract('contract', contract._id);
    };
    const hasEditingDraftForSupplierAcct = contract.hasDraft && contract.locationId && contract.viewingBy === 'Supplier';
    const hasEditingDraftForBuyerAcct = contract.viewingBy !== 'Supplier' && contract.buyerHasDraftId;
    const readOnly = !R.contains('write', contract.access || []) && !noReadAccess;
    // const iconToDisplay = (() => {
    //   if (Boolean(contract.locationName)) return 'tc-icon-location_on';
    // })();
    const fileExists = files && files.length;
    const externalRates = !locationId && (contract.buyerChecked || Boolean(fileExists));
    const unread = !viewerIsSender && !contract.isViewed;
    const otherSideRead = contract.viewedBy && contract.viewedBy.length;
    const showEmailAndContractStatus = (() => {
      // hide for received rates if it is a Buyer
      if (viewingBy !== 'Supplier' && locationId) return false;
      // hide for rate requests if it is a Supplier
      if (viewingBy === 'Supplier' && !locationId) return false;
      // rates received externally
      if (viewingBy !== 'Supplier' && externalRates) return false;
      return true;
    })();
    const stateChange = (newStatus) => {
      if (readOnly) extraProps.triggerNoAccessWarning();
      else actions.stateChange(contract._id, newStatus);
    };
    const requestText = (() => {
      if (tcContractsInRequest && viewingBy === 'Contractor') {
        return <span style={{ color: 'green' }}>{respondedContracts.length} {pluralize('contracts', respondedContracts.length)}</span>;
      }
      // if (fileExists && viewingBy === 'Contractor') {
      //   return <span style={{ color: 'green' }}>Received {files.length} {pluralize('files', files.length)}</span>;
      // }
      if (!locationId && viewingBy === 'Supplier' && shownState !== 'sent') {
        return <span>Prepared for {to.name}</span>;
      }
      return '';
    })();
    const {
      displayName,
      logoToDisplay,
    } = (() => {
      // display based on owners' partner list
      const partner = partners[0] || {};
      return {
        displayName: partner.name || contract.from.name,
        logoToDisplay: partner.avatar || contract.from.avatar,
      };
    })();
    const propsForActions = {
      disabled: noReadAccess,
      contract,
      templates,
      triggerNoAccessWarning: extraProps.triggerNoAccessWarning,
      actions: R.merge(actions, {
        downloadContractPdf: (anchorEl) => actions.downloadContractPdf({
          docId: tcContractsInRequest ? null : contract._id,
          files,
          contractIds: tcContractsInRequest ? respondedContracts : null,
          zipFileName: displayName,
          zipNameWhenFileExists: displayName,
          anchorEl,
        }),
      }),
      openManageFolderDialog: (folderAction) => () => actions.updateProps({
        manageFolderDialogOpen: true,
        contractFolders: contract.folders,
        folderAction,
      }),
      openMenu: () => actions.updateProps({ hoveringOverContractId: contract._id }),
      goIntoContractOrWizard,
    };
    const displayLoc = Boolean(locationId && displayName !== contract.locationName);
    return (
      <TableRow
        selectable={false}
        data-cy={`${contract._id}contractRow`}
        style={{ height: '56px', overflow: 'visible', backgroundColor: convertedFromExternal ? '#edfafc' : '#fff' }}
      >
        <TableRowColumn
          colSpan="1"
        >
          {
            noReadAccess ? (
              <LockIcon color="rgba(0, 0, 0, 0.44)" />
            ) : (
              <Checkbox
                onCheck={() => actions.selectAContract(contract._id)}
                defaultChecked={R.contains(contract._id, selectedContracts)}
              />
            )
          }
        </TableRowColumn>
        <TableRowColumn colSpan="1" style={{ padding: 0, overflow: 'visible' }}>
          {
            R.call(R.cond([
              [() => logoToDisplay,
                R.always(<Avatar src={logoToDisplay} style={{ marginTop: '4px' }}/>)],
              [R.T, R.always(<Avatar>{R.head(displayName || '')}</Avatar>)],
            ]))
          }
        </TableRowColumn>
        <TableRowColumn
          colSpan="6"
          style={{ padding: 0, color: noReadAccess ? 'rgba(0, 0, 0, 0.44)' : '', overflow: 'visible' }}
        >
          <div
            style={{ height: '48px', display: 'table-cell', verticalAlign: 'middle', cursor: noReadAccess ? '' : 'pointer' }}
            onClick={goIntoContractOrWizard}
          >
            <div style={{ fontWeight: unread ? 'bold' : '', lineHeight: '12px', fontSize: '12px' }} className="flex-center">
              {displayName}&nbsp;
              <small><i>{hasVersion ? `(v.${R.path(['versions', 'length'], contract) + 1})` : ''}</i></small>
              <small
                style={{ color: 'rgba(0, 0, 0, 0.44)' }}
                data-cy={`folder-${contract.folders.join(', ')}`}
              >{showFolder && !R.isEmpty(contract.folders) ? ` (${contract.folders.join(', ')})` : ''}</small>
              <small style={{ color: 'red' }}>{showFolder && contract.isDeleted ? 'Trash' : ''}</small>
              <small style={{ color: 'red' }}>{showFolder && contract.isArchived ? 'Archived' : ''}</small>
              {Boolean(contract.signatures && contract.signatures.length) && (
                <span className="hint--top" aria-label="Signed">
                  &nbsp;&nbsp;
                  <SignedIcon style={{ height: '18px', width: '18px', color: '#2e7d32' }} />
                </span>
              )}
            </div>
            <div style={{ fontWeight: unread ? 'bold' : '', display: 'flex', alignItems: 'center', lineHeight: '16px', fontSize: '12px' }}>
              {
                displayLoc && (
                  <FontIcon className="tc-icon-location_on" style={{ fontSize: '16px', color: 'rgba(0, 0, 0, 0.54)', marginRight: '8px' }} />
                )
              }
              {
                displayLoc && contract.locationName
              }
              {
                R.path(['supplierInfo', 'marketInfo'], contract) ? (<small>
                  &nbsp;{`(${R.path(['supplierInfo', 'marketInfo'], contract)})`}
                </small>) : <span/>
              }
              {requestText}
              <span style={{ color: 'rgba(0, 0, 0, 0.44)' }}> {readOnly && '(READ-ONLY)'}</span>
            </div>
            {
              hasEditingDraftForSupplierAcct ? (<div
                style={{ lineHeight: '10px', color: 'red', fontSize: '10px' }}
              >
              This contract has a&nbsp;
                <a href={`/contracting/draft/${contract._id}/details`} onClick={e => e.stopPropagation()}>
              draft
              </a>.
            </div>) : <span/>
            }{
              hasEditingDraftForBuyerAcct ? (<div
                style={{ lineHeight: '10px', color: 'red', fontSize: '10px' }}
              >
              This contract has a draft.
            </div>) : <span/>
            }
          </div>
        </TableRowColumn>
        {
          !hideStatusIcon && (
            <TableRowColumn
              colSpan="1"
              style={{ padding: 0, textAlign: 'center', overflow: 'visible' }}
            >
              <div>
                {
                  R.call(R.cond([
                    [() => shownState === 'sent' && billingPlan === 'basic', R.always(getIconEle({ status: 'unpaid', showEmailAndContractStatus, otherSideRead, sentState, companyType: viewingBy, emailStatus }))],
                    [() => shownState === 'sent', R.always(getIconEle({ showEmailAndContractStatus, status: shownState, sentState, companyType: viewingBy, overwriteColor: null, externalRates: contract.buyerChecked && externalRates, otherSideRead, emailStatus }))],
                    [() => viewingBy === 'Contractor' && (locationId || fileExists || tcContractsInRequest), R.always(getIconEle({
                      showEmailAndContractStatus,
                      status: locationId || tcContractsInRequest ? 'rates received' : 'rates received externally',
                      otherSideRead,
                      emailStatus,
                      convertedFromExternal,
                    }))],
                    [() => viewingBy === 'Contractor' && contract.buyerChecked, R.always(<span style={{ width: '35px', display: 'inline-block' }}>
                  <RatesReceivedIcon color={'rgba(0, 0, 0, 0.1)'} />
                  <span style={{ visibility: 'hidden' }}>?</span>
                </span>)],
                    [() => viewingBy === 'Supplier' && shownState !== 'sent', R.always(getIconEle({ showEmailAndContractStatus, status: shownState, otherSideRead, emailStatus }))],
                  ]))
                }
              </div>
            </TableRowColumn>
          )
        }
        <TableRowColumn
          colSpan="1"
          style={{ padding: 0 }}
        >
          {
            viewingBy === 'Contractor' ? (
              <FlatDropDownMenu
                button={
                  <SmallFlatButton
                    labelStyle={{ padding: 0 }}
                    icon={<ArrowDropDownIcon style={{ fill: 'rgba(0, 0, 0, 0.54)', margin: 0 }}/>}
                    labelPosition="before"
                    label={<span style={{ color: getStatusColor(shownState), width: '54px', display: 'inline-block' }}>{shownState}</span>}
                  />
                }
              >
                <MenuItem
                  primaryText="Mark as Sent"
                  style={{ color: getStatusColor('sent') }}
                  disabled={Boolean(shownState === 'sent' || locationId)}
                  onTouchTap={() => stateChange('sent')}
                />
                <MenuItem
                  primaryText="Mark as Received"
                  style={{ color: getStatusColor('received') }}
                  disabled={shownState === 'received'}
                  onTouchTap={() => stateChange('received')}
                />
                <MenuItem
                  primaryText="Mark as Accepted"
                  style={{ color: getStatusColor('accepted') }}
                  disabled={shownState === 'accepted'}
                  onTouchTap={() => stateChange('accepted')}
                />
                <MenuItem
                  primaryText="Mark as Loading"
                  style={{ color: getStatusColor('loading') }}
                  disabled={shownState === 'loading'}
                  onTouchTap={() => stateChange('loading')}
                />
                <MenuItem
                  primaryText="Mark as Loaded"
                  style={{ color: getStatusColor('loaded') }}
                  disabled={shownState === 'loaded'}
                  onTouchTap={() => stateChange('loaded')}
                />
                <MenuItem
                  primaryText="Mark as Rejected"
                  style={{ color: getStatusColor('rejected') }}
                  disabled={shownState === 'rejected'}
                  onTouchTap={() => stateChange('rejected')}
                />
              </FlatDropDownMenu>
            ) : <span style={{ color: getStatusColor(shownState) }}>{shownState}</span>
          }
        </TableRowColumn>
        {
          viewingBy === 'Contractor' && (
          <TableRowColumn
            colSpan="1"
            style={{ padding: 0, fontSize: '10px' }}
          >
            <UpdateAssignee
              assignee={contract.assignee}
              contractId={contract._id}
              isContainer={!Boolean(contract.locationId)}
              files={contract.files}
            />
          </TableRowColumn>
          )
        }
        <TableRowColumn
          colSpan="2"
          style={{
            padding: 0, textAlign: 'center',
          }}
        >
          <div style={{ display: 'flex' }}>
            <IconButton
              onClick={openContacts}
              style={{ padding: '12px 4px' }}
              iconStyle={{ color: 'rgba(0, 0, 0, 0.54)' }}
            >
              <FontIcon className="tc-icon-mail_outline" style={{ fontSize: '20px', top: '4px' }} />
            </IconButton>
            <IconButton
              onClick={() => actions.viewNotes('contract', contract._id)}
              style={{ padding: '12px 4px' }}
              iconStyle={{ color: 'rgba(0, 0, 0, 0.54)' }}
            >
              <FontIcon className="tc-icon-note_add" style={{ color: 'rgba(0, 0, 0, 0.54)' }} />
              { contract.notesCount
                && <RedDot style={{ top: '-10px', left: '5px' }} color="#00bcd4" />}
            </IconButton>
            <IconButton
              onClick={() => actions.viewActivities('contract', contract._id)}
              style={{ padding: '12px 4px' }}
              iconStyle={{ color: 'rgba(0, 0, 0, 0.54)' }}
            >
              <FontIcon className="tc-icon-question_answer" style={{ color: 'rgba(0, 0, 0, 0.54)' }} />
              { contract.hasNewComments && <RedDot style={{ top: '-10px', left: '6px' }} />}
            </IconButton>
          </div>
        </TableRowColumn>
        {
          showContractYear && (
            <TableRowColumn colSpan="1" style={{ padding: 0, textAlign: 'center', whiteSpace: 'pre-wrap', fontSize: '10px' }}>
              {`${moment.utc(contract.startDate).format('D MMM YY')} -\n${moment.utc(contract.endDate).format('D MMM YY')}  `}
            </TableRowColumn>
          )
        }
        <TableRowColumn colSpan="1" style={{ padding: 0, fontSize: '10px', textAlign: 'center' }}>
          {moment(contract.updatedAt).calendar(null, {
            sameDay: 'h:mm a',
            lastDay: 'D MMM YY',
            lastWeek: 'D MMM YY',
            sameElse: 'D MMM YY',
          })}
        </TableRowColumn>
        <TableRowColumn
          colSpan="1"
          style={{ padding: 0, textAlign: 'center', overflow: 'visible' }}
        >
          {
            forLandingPage ? (
              <SmallRaisedButton
                labelStyle={{ padding: '4px', top: 0 }}
                label="Download"
                secondary
              />
            ) : (
              <ActionButtons {...propsForActions} />
            )
          }
        </TableRowColumn>
      </TableRow>
    );
  };

export default ContractRowDumb;



// WEBPACK FOOTER //
// ./src/app/components/contracting/contract-list/list/row-dumb.jsx